import { 
  Building2, 
  FileText, 
  Handshake, 
  GitMerge, 
  Heart, 
  Landmark, 
  Scale, 
  ShieldCheck, 
  Globe, 
  Stamp, 
  Copyright 
} from 'lucide-react';

export const services = [
  {
    id: 'corporate-business-contracts',
    slug: 'corporate-business-contracts',
    title: 'Corporate & Business Contracts',
    shortDescription: 'Comprehensive contract drafting, review, and negotiation services for businesses of all sizes.',
    icon: FileText,
    category: 'Business Law',
    overview: 'Our corporate contract services provide end-to-end legal support for all your business agreement needs. From drafting initial contracts to negotiating complex multi-party agreements, we ensure your interests are protected at every stage.',
    benefits: [
      'Minimize legal risks with professionally drafted contracts',
      'Protect your intellectual property and trade secrets',
      'Ensure regulatory compliance across jurisdictions',
      'Reduce disputes with clear, enforceable terms',
      'Expert negotiation support for better outcomes',
      'Scalable contract frameworks that grow with your business'
    ],
    process: [
      { step: 1, title: 'Initial Consultation', description: 'We review your business needs and contract requirements' },
      { step: 2, title: 'Contract Drafting', description: 'Our team creates customized contracts tailored to your situation' },
      { step: 3, title: 'Review & Revision', description: 'We refine the documents based on your feedback' },
      { step: 4, title: 'Finalization', description: 'Final review and execution guidance provided' }
    ],
    faqs: [
      { question: 'What types of contracts do you handle?', answer: 'We handle all business contracts including partnership agreements, vendor contracts, employment agreements, NDAs, licensing agreements, and more.' },
      { question: 'How long does contract drafting take?', answer: 'Simple contracts can be completed within 3-5 business days. Complex agreements may take 2-3 weeks depending on the scope.' },
      { question: 'Can you review existing contracts?', answer: 'Yes, we provide comprehensive contract review services to identify risks and suggest improvements.' }
    ]
  },
  {
    id: 'corporate-business-formation',
    slug: 'corporate-business-formation',
    title: 'Corporate & Business Formation',
    shortDescription: 'Expert guidance on entity selection and business formation to set your venture up for success.',
    icon: Building2,
    category: 'Business Law',
    overview: 'Starting a business requires careful planning and the right legal structure. We guide entrepreneurs and established companies through entity formation, helping you choose the optimal structure for tax efficiency, liability protection, and operational flexibility.',
    benefits: [
      'Choose the right entity type for your goals',
      'Maximize tax advantages and liability protection',
      'Ensure proper corporate governance from day one',
      'Streamlined registration and filing process',
      'Ongoing compliance support available',
      'Multi-state registration and expansion guidance'
    ],
    process: [
      { step: 1, title: 'Business Assessment', description: 'We analyze your goals, structure, and growth plans' },
      { step: 2, title: 'Entity Recommendation', description: 'Expert advice on LLC, Corporation, Partnership, or other structures' },
      { step: 3, title: 'Formation Filing', description: 'We handle all state filings and documentation' },
      { step: 4, title: 'Post-Formation Setup', description: 'Operating agreements, bylaws, and initial compliance' }
    ],
    faqs: [
      { question: 'What is the difference between an LLC and Corporation?', answer: 'LLCs offer flexibility and pass-through taxation, while Corporations provide more structure and easier access to investors. We help you determine which is best for your situation.' },
      { question: 'How long does business formation take?', answer: 'Most formations are completed within 1-2 weeks, though expedited options are available in many states.' },
      { question: 'Do you help with foreign business registration?', answer: 'Yes, we assist with registering your business in multiple states and can guide international expansion.' }
    ]
  },
  {
    id: 'business-transaction',
    slug: 'business-transaction',
    title: 'Business Transaction',
    shortDescription: 'Strategic legal support for buying, selling, and structuring business deals.',
    icon: Handshake,
    category: 'Business Law',
    overview: 'Business transactions require meticulous attention to detail and strategic legal guidance. Our team provides comprehensive support for acquisitions, sales, joint ventures, and strategic partnerships, ensuring smooth transactions that protect your interests.',
    benefits: [
      'Thorough due diligence to uncover hidden risks',
      'Strategic deal structuring for optimal outcomes',
      'Expert negotiation to maximize value',
      'Comprehensive documentation and closing support',
      'Post-transaction integration assistance',
      'Confidential handling of sensitive business information'
    ],
    process: [
      { step: 1, title: 'Deal Assessment', description: 'We evaluate the transaction structure and your objectives' },
      { step: 2, title: 'Due Diligence', description: 'Comprehensive review of all business aspects' },
      { step: 3, title: 'Negotiation & Documentation', description: 'We negotiate terms and draft all agreements' },
      { step: 4, title: 'Closing & Integration', description: 'We manage the closing process and transition' }
    ],
    faqs: [
      { question: 'What does due diligence cover?', answer: 'Due diligence includes financial review, legal compliance, intellectual property, contracts, employment matters, environmental issues, and more.' },
      { question: 'How do you handle confidentiality?', answer: 'We implement strict confidentiality protocols including NDAs, data room management, and secure communication channels.' },
      { question: 'Can you help with deal financing?', answer: 'While we don\'t provide financing, we work with your financial advisors to structure deals and draft financing documentation.' }
    ]
  },
  {
    id: 'mergers-acquisitions',
    slug: 'mergers-acquisitions',
    title: 'Mergers & Acquisitions',
    shortDescription: 'Full-service M&A legal support from initial strategy through post-merger integration.',
    icon: GitMerge,
    category: 'Business Law',
    overview: 'Mergers and acquisitions are transformative events that require experienced legal counsel. We provide comprehensive M&A services for both buyers and sellers, from initial strategy development through closing and integration, ensuring your transaction achieves its strategic objectives.',
    benefits: [
      'Strategic guidance throughout the M&A lifecycle',
      'Rigorous due diligence and risk assessment',
      'Skilled negotiation for favorable terms',
      'Regulatory approval navigation',
      'Seamless post-merger integration support',
      'Tax-efficient deal structuring and planning'
    ],
    process: [
      { step: 1, title: 'Strategic Planning', description: 'Define objectives and identify targets or buyers' },
      { step: 2, title: 'Due Diligence', description: 'Comprehensive investigation of all material matters' },
      { step: 3, title: 'Deal Structuring', description: 'Optimize tax and legal structure of the transaction' },
      { step: 4, title: 'Closing & Integration', description: 'Execute the deal and manage transition' }
    ],
    faqs: [
      { question: 'How long does an M&A transaction typically take?', answer: 'Timelines vary significantly based on deal complexity, but most transactions take 3-6 months from letter of intent to closing.' },
      { question: 'What regulatory approvals might be needed?', answer: 'Depending on the industry and deal size, approvals may be required from antitrust authorities, industry regulators, or foreign investment review bodies.' },
      { question: 'Do you represent both buyers and sellers?', answer: 'Yes, we represent clients on both sides of transactions, though never both parties in the same deal.' }
    ]
  },
  {
    id: 'non-profit-law',
    slug: 'non-profit-law',
    title: 'Non-Profit Law',
    shortDescription: 'Dedicated legal services for non-profit organizations and charitable foundations.',
    icon: Heart,
    category: 'Compliance & Protection',
    overview: 'Non-profit organizations face unique legal challenges that require specialized expertise. We support charitable organizations, foundations, and social enterprises with formation, tax-exempt status, governance, and ongoing compliance to help you focus on your mission.',
    benefits: [
      '501(c)(3) and other tax-exempt status applications',
      'Board governance and fiduciary duty guidance',
      'Grant compliance and donor agreements',
      'State charitable registration assistance',
      'Annual compliance and reporting support',
      'Fundraising and solicitation law guidance'
    ],
    process: [
      { step: 1, title: 'Mission Planning', description: 'We help define your organizational structure and purpose' },
      { step: 2, title: 'Formation & Exemption', description: 'State incorporation and IRS tax-exempt application' },
      { step: 3, title: 'Governance Setup', description: 'Bylaws, policies, and board training' },
      { step: 4, title: 'Ongoing Compliance', description: 'Annual filings, audits, and regulatory guidance' }
    ],
    faqs: [
      { question: 'How long does it take to get 501(c)(3) status?', answer: 'The IRS typically processes applications within 3-6 months, though complex cases may take longer.' },
      { question: 'What ongoing compliance is required?', answer: 'Non-profits must file annual Form 990, maintain proper governance, and comply with state charitable registration requirements.' },
      { question: 'Can a non-profit engage in political activities?', answer: '501(c)(3) organizations have strict limitations on political activities. We can advise on permissible advocacy and lobbying.' }
    ]
  },
  {
    id: 'government-contracting',
    slug: 'government-contracting',
    title: 'Government Contracting',
    shortDescription: 'Navigate the complex world of federal, state, and local government contracts.',
    icon: Landmark,
    category: 'Compliance & Protection',
    overview: 'Government contracting offers significant opportunities but comes with complex regulations and compliance requirements. Our team helps contractors navigate the procurement process, ensure compliance, and resolve disputes with government agencies.',
    benefits: [
      'Procurement process guidance and bid support',
      'Contract negotiation and compliance',
      'Small business certification assistance',
      'Dispute resolution and claims support',
      'Security clearance and classified work guidance',
      'Subcontractor agreement drafting and review'
    ],
    process: [
      { step: 1, title: 'Readiness Assessment', description: 'Evaluate your capacity for government work' },
      { step: 2, title: 'Registration & Certification', description: 'SAM registration and small business certifications' },
      { step: 3, title: 'Proposal Support', description: 'Bid preparation and compliance review' },
      { step: 4, title: 'Contract Performance', description: 'Ongoing compliance and modification support' }
    ],
    faqs: [
      { question: 'What certifications can help win contracts?', answer: 'Programs like 8(a), HUBZone, WOSB, SDVOSB, and VOSB provide competitive advantages. We help determine eligibility and obtain certifications.' },
      { question: 'How do you handle bid protests?', answer: 'We represent contractors in protests before the GAO, COFC, and agency-level forums when procurement irregularities occur.' },
      { question: 'What compliance programs are required?', answer: 'Requirements vary by contract type and value, but may include FAR/DFARS compliance, DCAA audits, and cybersecurity standards.' }
    ]
  },
  {
    id: 'business-disputes',
    slug: 'business-disputes',
    title: 'Business Disputes',
    shortDescription: 'Effective resolution of commercial disputes through negotiation, mediation, or litigation.',
    icon: Scale,
    category: 'Compliance & Protection',
    overview: 'Business disputes can threaten your operations, reputation, and bottom line. We provide strategic representation in all types of commercial disputes, always seeking the most efficient resolution while protecting your interests and business relationships.',
    benefits: [
      'Strategic assessment of dispute resolution options',
      'Skilled negotiation to avoid costly litigation',
      'Expert mediation and arbitration representation',
      'Aggressive litigation when necessary',
      'Focus on practical business outcomes',
      'Preservation of key business relationships during disputes'
    ],
    process: [
      { step: 1, title: 'Case Evaluation', description: 'We assess the dispute and your objectives' },
      { step: 2, title: 'Strategy Development', description: 'Create a tailored resolution approach' },
      { step: 3, title: 'Resolution Pursuit', description: 'Negotiation, mediation, or litigation as appropriate' },
      { step: 4, title: 'Resolution & Recovery', description: 'Finalize settlement or enforce judgment' }
    ],
    faqs: [
      { question: 'Should I try to settle or go to court?', answer: 'We analyze each situation individually. Settlement often saves time and money, but litigation may be necessary to protect your rights or send a message.' },
      { question: 'What is the difference between mediation and arbitration?', answer: 'Mediation is a facilitated negotiation where you retain control. Arbitration is a binding process where an arbitrator makes the decision.' },
      { question: 'How long do business disputes typically take?', answer: 'Timelines vary greatly. Negotiations may resolve in weeks, while complex litigation can take years. We work to resolve matters efficiently.' }
    ]
  },
  {
    id: 'anti-corruption-compliance',
    slug: 'anti-corruption-compliance',
    title: 'Anti-Corruption Compliance',
    shortDescription: 'Protect your business with robust FCPA and global anti-corruption compliance programs.',
    icon: ShieldCheck,
    category: 'Compliance & Protection',
    overview: 'Operating globally requires robust anti-corruption compliance. We help companies develop, implement, and maintain compliance programs that satisfy the Foreign Corrupt Practices Act (FCPA), UK Bribery Act, and other international anti-corruption laws.',
    benefits: [
      'Comprehensive compliance program development',
      'Third-party due diligence procedures',
      'Employee training and awareness programs',
      'Internal investigation support',
      'Government inquiry response',
      'Cross-border transaction risk mitigation'
    ],
    process: [
      { step: 1, title: 'Risk Assessment', description: 'Identify corruption risks in your operations' },
      { step: 2, title: 'Program Design', description: 'Develop policies, procedures, and controls' },
      { step: 3, title: 'Implementation', description: 'Training, communication, and rollout' },
      { step: 4, title: 'Monitoring & Improvement', description: 'Ongoing audits and program enhancement' }
    ],
    faqs: [
      { question: 'What is the FCPA?', answer: 'The Foreign Corrupt Practices Act prohibits bribing foreign officials and requires accurate books and records. Violations can result in significant penalties.' },
      { question: 'Do I need a compliance program?', answer: 'If you operate internationally or work with government entities, a compliance program is essential to manage risk and demonstrate good faith.' },
      { question: 'What should I do if I discover potential violations?', answer: 'Contact us immediately. Prompt investigation and appropriate remediation can significantly reduce potential penalties.' }
    ]
  },
  {
    id: 'immigration',
    slug: 'immigration',
    title: 'Immigration',
    shortDescription: 'Business immigration services to help you hire and retain global talent.',
    icon: Globe,
    category: 'Compliance & Protection',
    overview: 'In today\'s global economy, access to international talent is crucial. Our business immigration practice helps companies navigate visa processes, compliance requirements, and workforce planning to build and maintain a diverse, skilled workforce.',
    benefits: [
      'Work visa petitions (H-1B, L-1, O-1, etc.)',
      'Green card sponsorship and processing',
      'I-9 compliance and audit support',
      'Immigration due diligence for M&A',
      'Global mobility strategy development',
      'PERM labor certification process management'
    ],
    process: [
      { step: 1, title: 'Strategy Consultation', description: 'Assess visa options and develop a plan' },
      { step: 2, title: 'Application Preparation', description: 'Gather documentation and prepare filings' },
      { step: 3, title: 'Submission & Monitoring', description: 'File applications and track progress' },
      { step: 4, title: 'Approval & Compliance', description: 'Manage ongoing status and renewals' }
    ],
    faqs: [
      { question: 'What visa options are available for skilled workers?', answer: 'Common options include H-1B for specialty occupations, L-1 for intracompany transfers, and O-1 for individuals with extraordinary ability.' },
      { question: 'How long does the green card process take?', answer: 'Timelines vary significantly based on country of birth and visa category, ranging from under a year to over a decade.' },
      { question: 'What are the I-9 compliance requirements?', answer: 'Employers must verify employment authorization for all employees within 3 days of hire and maintain proper records.' }
    ]
  },
  {
    id: 'trademark-registration',
    slug: 'trademark-registration',
    title: 'Trademark Registration',
    shortDescription: 'Protect your brand with comprehensive trademark search, registration, and enforcement.',
    icon: Stamp,
    category: 'Intellectual Property',
    overview: 'Your brand is one of your most valuable assets. We provide full-service trademark protection, from clearance searches and registration through monitoring and enforcement, helping you build and protect strong brands in the United States and internationally.',
    benefits: [
      'Comprehensive trademark clearance searches',
      'Strategic filing and prosecution',
      'Portfolio management and renewals',
      'Monitoring and enforcement services',
      'International trademark protection',
      'Opposition and cancellation proceeding representation'
    ],
    process: [
      { step: 1, title: 'Clearance Search', description: 'Comprehensive search for conflicting marks' },
      { step: 2, title: 'Application Filing', description: 'Prepare and file USPTO applications' },
      { step: 3, title: 'Prosecution', description: 'Respond to office actions and objections' },
      { step: 4, title: 'Registration & Maintenance', description: 'Secure registration and maintain rights' }
    ],
    faqs: [
      { question: 'How long does trademark registration take?', answer: 'The USPTO process typically takes 8-12 months if there are no complications. Complex cases may take longer.' },
      { question: 'Do I need to register to have trademark rights?', answer: 'Common law rights exist through use, but federal registration provides significant advantages including nationwide priority and statutory damages.' },
      { question: 'Can I protect my trademark internationally?', answer: 'Yes, we can help with international protection through the Madrid Protocol or direct filings in individual countries.' }
    ]
  },
  {
    id: 'copyright-registration',
    slug: 'copyright-registration',
    title: 'Copyright Registration',
    shortDescription: 'Secure and enforce your creative works with professional copyright services.',
    icon: Copyright,
    category: 'Intellectual Property',
    overview: 'Copyright protection is essential for creators and content-driven businesses. We help clients register copyrights, draft licensing agreements, and enforce their rights against infringement, ensuring your creative works are properly protected and monetized.',
    benefits: [
      'Copyright registration with the US Copyright Office',
      'Work-for-hire and assignment agreements',
      'Licensing and royalty arrangements',
      'DMCA compliance and takedown procedures',
      'Infringement litigation and enforcement',
      'Digital content protection and anti-piracy strategies'
    ],
    process: [
      { step: 1, title: 'Rights Assessment', description: 'Identify copyrightable works and ownership' },
      { step: 2, title: 'Registration', description: 'File applications with the Copyright Office' },
      { step: 3, title: 'Licensing Setup', description: 'Create agreements for authorized use' },
      { step: 4, title: 'Enforcement', description: 'Monitor and act against infringement' }
    ],
    faqs: [
      { question: 'What can be copyrighted?', answer: 'Copyright protects original works of authorship including literary works, software, music, artwork, photographs, and videos.' },
      { question: 'Do I need to register to have copyright protection?', answer: 'Copyright exists automatically upon creation, but registration is required before filing suit and enables statutory damages and attorney fees.' },
      { question: 'What should I do if someone infringes my copyright?', answer: 'Options include DMCA takedown notices, cease and desist letters, negotiated settlements, or litigation. We help determine the best approach.' }
    ]
  }
];

export const serviceCategories = [
  { name: 'Business Law', services: services.filter(s => s.category === 'Business Law') },
  { name: 'Compliance & Protection', services: services.filter(s => s.category === 'Compliance & Protection') },
  { name: 'Intellectual Property', services: services.filter(s => s.category === 'Intellectual Property') }
];

export const getServiceBySlug = (slug) => services.find(s => s.slug === slug);
